import { motion } from "framer-motion";
import SiteNavbar from "../components/SiteNavbar";
import heroAboutLight from "../assets/images/hero-about-light-pexels.jpeg";
import heroAboutDark from "../assets/images/hero-about-dark-pexels.jpeg";
import teamImg from "../assets/images/startup-team.jpeg";

export default function AboutPage() {
  return (
    <div className="flex min-h-screen flex-col bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-50">
      <SiteNavbar />

      {/* Hero banner */}
      <div className="relative h-56 w-full md:h-72">
        {/* Theme-specific hero images */}
        <img
          src={heroAboutLight}
          alt="Teacher explaining concept to students in bright classroom"
          className="theme-light-only absolute inset-0 h-full w-full object-cover"
        />
        <img
          src={heroAboutDark}
          alt="Educator working late in dimly lit classroom"
          className="theme-dark-only absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-white/70 via-white/40 to-white dark:from-gray-900/70 dark:via-gray-900/40 dark:to-gray-900" />
        {/* Credits – light */}
        <p className="absolute bottom-2 right-4 text-xs text-gray-500 dark:hidden">
          Photo by{' '}
          <a
            href="https://www.pexels.com/@buro-millennial"
            target="_blank"
            rel="noreferrer"
            className="underline"
          >
            Buro Millennial
          </a>{' '}
          on{' '}
          <a href="https://www.pexels.com" target="_blank" rel="noreferrer" className="underline">
            Pexels
          </a>
        </p>

        {/* Credits – dark */}
        <p className="absolute bottom-2 right-4 hidden text-xs text-gray-400 dark:block">
          Photo by{' '}
          <a
            href="https://www.pexels.com/@rdne"
            target="_blank"
            rel="noreferrer"
            className="underline"
          >
            RDNE Stock project
          </a>{' '}
          on{' '}
          <a href="https://www.pexels.com" target="_blank" rel="noreferrer" className="underline">
            Pexels
          </a>
        </p>
      </div>

      <section className="mx-auto w-full max-w-4xl flex-1 px-6 py-24">
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-6 text-4xl font-extrabold tracking-tight text-gray-900 dark:text-gray-50"
        >
          Our story
        </motion.h1>

        <p className="mb-4 text-lg text-gray-700 dark:text-gray-300">
          We’re a small team of academics, learning designers and engineers who hit breaking point with the ever-growing
          pile of marking. In late&nbsp;2023 we built a weekend prototype that flagged reference issues and style-guide
          slip-ups automatically. The idea resonated, so we kept building — with a single goal: give educators more
          face-to-face time with students, not less.
        </p>

        <p className="mb-4 text-lg text-gray-700 dark:text-gray-300">
          We’re now running a private pilot with a handful of universities and colleges. Everything you see in the
          product is shaped by their real-world marking sessions, staff meetings and student feedback loops.
        </p>
      </section>

      {/* Timeline */}
      <section className="bg-gray-50 dark:bg-gray-800 py-24">
        <div className="mx-auto w-full max-w-6xl px-6">
          <h2 className="mb-12 text-3xl font-bold tracking-tight text-gray-900 dark:text-gray-50 md:text-4xl text-center">Milestones so far</h2>

          <ol className="relative border-l border-gray-300 pl-6">
            {[
              { date: 'Oct 2023', title: 'First prototype', desc: 'A weekend hack-day turned into a tool that checked citations automatically.' },
              { date: 'Mar 2024', title: 'Private pilot opens', desc: 'Five lecturers across three institutions start using the alpha on live assessments.' },
              { date: 'Today', title: 'Seeking founding partners', desc: 'We’re expanding the pilot and gathering feedback before a public launch.' },
            ].map((item, idx) => (
              <li key={item.title} className="mb-10 ml-4">
                <div className="absolute -left-1.5 mt-1.5 h-3 w-3 rounded-full bg-primary" />
                <time className="mb-1 text-sm font-medium leading-none text-gray-600 dark:text-gray-400">{item.date}</time>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-50">{item.title}</h3>
                <p className="text-gray-700 dark:text-gray-300">{item.desc}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* Team photo */}
      <section className="mx-auto w-full max-w-6xl px-6 py-24 dark:text-gray-50">
        <div className="grid gap-12 md:grid-cols-2 md:items-center">
          <img src={teamImg} alt="ItsAcademic founding team in the office" className="rounded-lg shadow-lg" />
          <div>
            <h2 className="mb-4 text-3xl font-bold tracking-tight text-gray-900 dark:text-gray-50">Small team. Big ambitions.</h2>
            <p className="mb-4 text-gray-700 dark:text-gray-300">
              We keep the org chart flat and the feedback loops even flatter. Everyone talks to customers, everyone ships
              code. If the mission resonates and you want to build for educators at global scale, we’re hiring.
            </p>
            <a
              href="mailto:jobs@itsacademic.ai"
              className="rounded-md bg-primary px-6 py-3 text-sm font-semibold text-white shadow hover:bg-primary-dark focus:outline-hidden focus:ring-2 focus:ring-primary focus:ring-offset-2 dark:focus:ring-offset-gray-900"
            >
              View open roles
            </a>
          </div>
        </div>
        <p className="mt-4 text-right text-xs text-gray-500 dark:text-gray-400">
          Photo by{' '}
          <a href="https://www.pexels.com/@fauxels" target="_blank" rel="noreferrer" className="underline">
            fauxels
          </a>{' '}
          on{' '}
          <a href="https://www.pexels.com" target="_blank" rel="noreferrer" className="underline">
            Pexels
          </a>
        </p>
      </section>

      {/* Mission */}
      <section className="bg-primary py-24 text-white">
        <div className="mx-auto w-full max-w-4xl px-6 text-center">
          <h2 className="mb-6 text-3xl font-bold md:text-4xl">The mission</h2>
          <p className="text-lg leading-relaxed text-white/90">
            Free up human attention so that educators can spend their energy mentoring, motivating and inspiring — not
            wrestling with spreadsheets. Every line of code we write chases that outcome.
          </p>
        </div>
      </section>

      <footer className="border-t bg-gray-50 dark:bg-gray-900 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
        © {new Date().getFullYear()} ItsAcademic.ai — created on Gadigal and Wangal Country by the team at Aperim Pty
        Ltd.
      </footer>
    </div>
  );
}
